import { toastError } from 'src/utils/toast';
import { fetchCatchImage } from 'src/api/api2';

function sanitizeFilename(name) {
  return String(name || 'image').replace(/[\\/:*?"<>|]/g, '_');
}

function ensureFilenameExtension(filename, fileUrl) {
  if (/\.[a-z0-9]{2,5}$/i.test(filename)) {
    return filename;
  }

  try {
    const pathname = new URL(fileUrl).pathname;
    const match = pathname.match(/\.(png|jpe?g|gif|webp)$/i);
    if (match) {
      return `${filename}.${match[1].toLowerCase()}`;
    }
  } catch {
    // Ignore invalid URLs and keep the original filename.
  }

  return filename;
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Navigate to an S3 URL that already has Content-Disposition: attachment.
 * The browser downloads the file and stays on the current page (no new tab).
 */
export function triggerAttachmentDownload(url) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export async function downloadImageFile(image, index = 0) {
  const fileUrl = image?.file_url;
  const filename = ensureFilenameExtension(
    sanitizeFilename(image?.name || `image-${index + 1}`),
    fileUrl,
  );

  try {
    if (image?.file instanceof Blob) {
      triggerBlobDownload(image.file, filename);
      return;
    }

    if (!fileUrl) {
      return;
    }

    if (fileUrl.startsWith('blob:') || fileUrl.startsWith('data:')) {
      const response = await fetch(fileUrl);
      triggerBlobDownload(await response.blob(), filename);
      return;
    }

    toastError(__('Download failed'));
  } catch (error) {
    toastError(__('Download failed'));
    console.error(error);
  }
}

export async function downloadCatchImageFile({ catchId, image, index = 0, token }) {
  try {
    if (image?.file instanceof Blob || image?.file_url?.startsWith('blob:') || image?.file_url?.startsWith('data:')) {
      await downloadImageFile(image, index);
      return;
    }

    if (!catchId || image?.id == null) {
      toastError(__('Download failed'));
      return;
    }

    const item = await fetchCatchImage({ id: catchId, image_id: image.id }, token);
    const downloadUrl = item?.download_url;
    if (!downloadUrl) {
      toastError(__('Download failed'));
      return;
    }

    triggerAttachmentDownload(downloadUrl);
  } catch (error) {
    toastError(__('Download failed'));
    console.error(error);
  }
}
